import React, { useRef, useState } from "react";
import { motion } from "framer-motion";

export default function ChartFlashRevealPrototype() {
  const chartRef = useRef(null);
  const [placed, setPlaced] = useState(null);     // "up" | "pullback" | "down"
  const [result, setResult] = useState(null);     // "correct" | "wrong" | null
  const [xp, setXp] = useState(190);
  const [tokens, setTokens] = useState(310);

  // For now we hardcode the correct answer; later pass via props
  const correctAnswer = "pullback";

  const handleDrop = (choice, event, info) => {
    if (!chartRef.current) return;

    const chartBox = chartRef.current.getBoundingClientRect();
    const droppedX = info.point.x;
    const droppedY = info.point.y;
    const inside =
      droppedX >= chartBox.left &&
      droppedX <= chartBox.right &&
      droppedY >= chartBox.top &&
      droppedY <= chartBox.bottom;

    if (!inside) return; // snap back (dragSnapToOrigin handles it)

    // Accept the drop
    setPlaced(choice);
    const ok = choice === correctAnswer;
    setResult(ok ? "correct" : "wrong");
    if (ok) {
      setXp((v) => v + 20);
      setTokens((v) => v + 15);
      // lightweight confetti burst (DOM-only)
      spawnConfetti(chartRef.current);
    }
  };

  return (
    <div style={styles.page}>
      {/* tiny header chips to mimic your UI */}
      <div style={styles.hud}>
        <div style={styles.badge}>🐂 FinTorro</div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={styles.pill}>🪙 Tokens: {tokens}</div>
          <div style={styles.pill}>✨ XP: {xp}</div>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.title}>Chart Flash Reveal</h2>

        {/* CHART AREA (placeholder for now) */}
        <div ref={chartRef} style={styles.chartArea}>
          <div style={styles.chartCanvas}>📊 Chart Here</div>

          {/* prediction marker */}
          {placed && (
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`prediction ${result}`}
              style={styles.prediction}
            >
              {placed === "up" ? "📈" : placed === "pullback" ? "↩" : "📉"}
            </motion.div>
          )}

          {/* outcome overlay when correct (simple forward bars) */}
          {result === "correct" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={styles.outcome}
            >
              ✅ Nice read! (price retests support, then continues)
            </motion.div>
          )}
        </div>

        {/* DRAGGABLE CHOICES */}
        <div style={styles.choices}>
          {[
            { id: "up", label: "📈 Continuation" },
            { id: "pullback", label: "↩ Pullback" },
            { id: "down", label: "📉 Reversal" },
          ].map((c) => (
            <motion.div
              key={c.id}
              drag
              dragSnapToOrigin
              whileTap={{ scale: 0.96 }}
              onDragEnd={(event, info) => handleDrop(c.id, event, info)}
              style={styles.chip}
            >
              {c.label}
            </motion.div>
          ))}
        </div>

        {/* FEEDBACK */}
        {result && (
          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            style={{
              marginTop: 12,
              fontWeight: 800,
              color: result === "correct" ? "#2ECC71" : "#FF4C4C",
            }}
            aria-live="polite"
          >
            {result === "correct" ? "🎉 Correct! +20 XP, +15 Tokens" : "❌ Not quite. The correct idea was: Pullback to support."}
          </motion.div>
        )}
      </div>

      {/* inline styles for quick drop-in */}
      <style>{`
        .prediction.correct { filter: drop-shadow(0 6px 18px rgba(46,204,113,0.5)); }
        .prediction.wrong { animation: shake 300ms ease-in-out; }
        @keyframes shake {
          0%,100%{ transform: translateX(0) }
          25%{ transform: translateX(-5px) }
          50%{ transform: translateX(5px) }
          75%{ transform: translateX(-3px) }
        }
        .confetti-piece {
          position: absolute;
          width: 6px;
          height: 10px;
          border-radius: 2px;
          opacity: 0.9;
          animation: fall 900ms ease-out forwards;
        }
        @keyframes fall {
          0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(60px) rotate(240deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/** DOM-only confetti burst (no <canvas>) */
function spawnConfetti(container, count = 24) {
  const colors = ["#FF7C24", "#2ECC71", "#FFB800", "#FFFFFF"];
  const box = container.getBoundingClientRect();
  for (let i = 0; i < count; i++) {
    const piece = document.createElement("div");
    piece.className = "confetti-piece";
    piece.style.left = `${box.width / 2 + (Math.random() * 80 - 40)}px`;
    piece.style.top = `16px`;
    piece.style.background = colors[i % colors.length];
    piece.style.transform = `rotate(${Math.random() * 180}deg)`;
    container.appendChild(piece);
    // cleanup
    setTimeout(() => piece.remove(), 1000);
  }
}

const styles = {
  page: { background: "#0B0D11", minHeight: "100vh", padding: 16, color: "#DDE2EA" },
  hud: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  badge: { fontWeight: 800 },
  pill: {
    background: "#12151B",
    border: "1px solid #1E232B",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 13,
  },
  card: {
    maxWidth: 960,
    margin: "20px auto",
    background: "#0F141B",
    border: "1px solid #1E2630",
    borderRadius: 16,
    padding: 16,
    textAlign: "center",
  },
  title: { color: "#FF7C24", fontSize: 24, margin: "6px 0 12px" },
  chartArea: {
    position: "relative",
    background: "#0E0F13",
    border: "1px solid #1C2128",
    borderRadius: 14,
    padding: 16,
    minHeight: 220,
  },
  chartCanvas: {
    height: 200,
    borderRadius: 10,
    background:
      "linear-gradient(180deg,#0E0F13 0%,#0F141B 100%)",
    border: "1px dashed #2A3038",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    color: "#AEB7C2",
  },
  prediction: {
    position: "absolute",
    top: 12,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: 28,
  },
  outcome: {
    position: "absolute",
    bottom: 8,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: 14,
    background: "#0F1A12",
    color: "#B7FFCF",
    border: "1px solid #214B35",
    padding: "6px 10px",
    borderRadius: 10,
  },
  choices: { display: "flex", justifyContent: "center", gap: 10, marginTop: 14, flexWrap: "wrap" },
  chip: {
    background: "#232830",
    padding: "10px 14px",
    borderRadius: 12,
    cursor: "grab",
    boxShadow: "0 3px 10px rgba(0,0,0,0.35)",
    userSelect: "none",
    fontWeight: 700,
    fontSize: 14,
  },
};
